import React from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"
import { NavLink } from "react-router-dom"

import * as TeamApi from "features/api/TeamApi"

import { AsideNav, CGUModal } from "layouts/Partials"
import { useTheme, useAuth, useModal, useAlert } from "hooks"

import {
	ADMIN_GENDERS_LIST,
	MFC_OPERATOR,
	BC_OPERATOR,
	MC_OPERATOR,
	MFC_CGU_VERSION,
	BC_CGU_VERSION,
	MC_CGU_VERSION,
} from "utils/constants"
import { isNotEmpty } from "utils"
import dateService from "services/date"

const ProtectedLayout = ({ children, className }) => {
	const [isMFCCGUValidated, setIsMFCCGUValidated] = React.useState(true)
	const [isBCCGUValidated, setIsBCCGUValidated] = React.useState(true)
	const [isMCCGUValidated, setIsMCCGUValidated] = React.useState(true)

	const { currentTheme } = useTheme()
	const { showModal } = useModal()
	const {
		entity,
		user,
		fetchUser,
		isMFCOperator,
		isBCOperator,
		isMCOperator,
		isUserDataCompleted,
	} = useAuth()
	const { alert } = useAlert()

	const isAdminUser = ADMIN_GENDERS_LIST.includes(user?.gender)

	const isCGUUpToDateAndSigned = (currentOperator, operatorCguVersion) => {
		// For each operator, we have to know if a cgu exists, the date of signature and the version signed by partner
		return (
			isNotEmpty(
				user?.description?.cgu?.find(
					(op) =>
						op.operator === currentOperator &&
						isNotEmpty(op.accepted_at) &&
						op.version === operatorCguVersion
				)
			) ?? false
		)
	}

	React.useEffect(() => {
		// A partner can have several operators
		if (!isAdminUser) {
			if (isMFCOperator)
				setIsMFCCGUValidated(isCGUUpToDateAndSigned(MFC_OPERATOR, MFC_CGU_VERSION))
			if (isBCOperator)
				setIsBCCGUValidated(isCGUUpToDateAndSigned(BC_OPERATOR, BC_CGU_VERSION))
			if (isMCOperator)
				setIsMCCGUValidated(isCGUUpToDateAndSigned(MC_OPERATOR, MC_CGU_VERSION))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entity, user])

	React.useEffect(() => {
		// No CGU modal for admin
		if (!isAdminUser) {
			// We can't open several modals at the same time. First, it's MFC, then BC ...
			if (!isMFCCGUValidated) CGUAcceptedModal(MFC_OPERATOR, MFC_CGU_VERSION)
			if (!isBCCGUValidated && isMFCCGUValidated) {
				CGUAcceptedModal(BC_OPERATOR, BC_CGU_VERSION)
			}
			if (!isMCCGUValidated) CGUAcceptedModal(MC_OPERATOR, MC_CGU_VERSION)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAdminUser, isMFCCGUValidated, isBCCGUValidated, isMCCGUValidated])

	const CGUAcceptedModal = async (operator, cguVersion) => {
		showModal(
			CGUModal,
			{
				operator: operator,
				callbackAndSend: async () => {
					try {
						const updatedCgu = [
							{
								operator: operator,
								version: cguVersion,
								accepted_at: dateService.getCurrentDateUTC("yyyy-MM-dd HH:mm:ss"),
							},
						]
						const updatedUser = {
							user_id: user.user_id,
							row_version: user.row_version,
							cgu: updatedCgu,
						}

						const { res, getError } = await TeamApi.putProfil(updatedUser)
						if (getError()) throw getError()
						if (res) await fetchUser(user.user_id, entity.entity_id)
					} catch (error) {
						console.error(error)
						const erroMsg = error?.message?.toString()
						if (erroMsg) alert("error", erroMsg)
					}
				},
			},
			{
				authorizedClosingModal: false,
				style: { maxWidth: "800px" },
			}
		)
	}

	if (!isUserDataCompleted || !user) return null

	return (
		<>
			{Boolean(currentTheme) ? (
				<div className={"l-logged"}>
					<AsideNav />
					<div
						className={clsx(" bg-white z-20 ", {
							"l-logged--mobile": isMobileOnly,
							"l-logged__main": !isMobileOnly,
						})}
					>
						<div className={clsx(className ? className : "")}>{children}</div>
					</div>
				</div>
			) : (
				<div className={"u-sticky-view"}>
					<div className="c-main-content flex justify-center items-center">
						<div className="c-spinner"></div>
					</div>
				</div>
			)}
		</>
	)
}

export default ProtectedLayout
